import React from 'react';
import { Button, ButtonProps } from '@chakra-ui/react';
import { useCoachmarks, TourId } from './CoachmarkProvider';

/**
 * Props for the TourTrigger component
 */
interface TourTriggerProps extends Omit<ButtonProps, 'onClick'> {
    tourId: TourId;
    onTourStart?: (tourId: TourId) => void;
    disabled?: boolean;
}

/**
 * TourTrigger - A button component that starts guided tours
 * 
 * Features:
 * - Integrates with CoachmarkProvider
 * - OMNI design system styling
 * - Accessible button behavior
 * - Event tracking support
 */
export const TourTrigger: React.FC<TourTriggerProps> = ({
    tourId,
    onTourStart,
    disabled = false,
    children,
    ...buttonProps
}) => {
    const { startTour, isRunning, activeTour } = useCoachmarks();

    const handleClick = () => {
        if (!disabled && !isRunning) {
            startTour(tourId);
            onTourStart?.(tourId);
        }
    };

    // Disable if currently running a tour
    const isDisabled = disabled || isRunning || activeTour === tourId;

    return (
        <Button
            onClick={handleClick}
            disabled={isDisabled}
            variant="outline"
            colorScheme="indigo"
            {...buttonProps}
        >
            {children || 'Start Tour'}
        </Button>
    );
};

/**
 * Props for tour control buttons
 */
type TourControlProps = Omit<ButtonProps, 'onClick'>;

/**
 * StopTourButton - Button to stop the current tour
 */
export const StopTourButton: React.FC<TourControlProps> = ({
    children,
    ...buttonProps
}) => {
    const { stopTour, isRunning } = useCoachmarks();

    if (!isRunning) {
        return null;
    }

    return (
        <Button
            onClick={stopTour}
            variant="ghost"
            colorScheme="red"
            size="sm"
            {...buttonProps}
        >
            {children || 'Stop Tour'}
        </Button>
    );
};

/**
 * PauseTourButton - Button to pause/resume the current tour
 */
export const PauseTourButton: React.FC<TourControlProps> = ({
    children,
    ...buttonProps
}) => {
    const { pauseTour, resumeTour, isRunning, run } = useCoachmarks();

    if (!isRunning && !run) {
        return null;
    }

    const handleClick = () => {
        if (run) {
            pauseTour();
        } else {
            resumeTour();
        }
    };

    return (
        <Button
            onClick={handleClick}
            variant="ghost"
            colorScheme="blue"
            size="sm"
            {...buttonProps}
        >
            {children || (run ? 'Pause Tour' : 'Resume Tour')}
        </Button>
    );
};

/**
 * ResetTourButton - Button to reset the current tour to the beginning
 */
export const ResetTourButton: React.FC<TourControlProps> = ({
    children,
    ...buttonProps
}) => {
    const { resetTour, activeTour } = useCoachmarks();

    if (!activeTour) {
        return null;
    }

    return (
        <Button
            onClick={resetTour}
            variant="ghost"
            colorScheme="orange"
            size="sm"
            {...buttonProps}
        >
            {children || 'Restart Tour'}
        </Button>
    );
};
